import React, { useEffect, useState, useMemo } from 'react'
import { Card, List, Button, Typography, Space, Tag, Empty, Row, Col, App, Modal, Skeleton } from 'antd'
import { 
  VideoCameraOutlined, 
  PictureOutlined, 
  DownloadOutlined, 
  DeleteOutlined,
  EyeOutlined,
  SyncOutlined,
  HistoryOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useSearchParams } from 'react-router-dom'
import { on as onEvent } from '@/services/events'
import { apiClient } from '@/services/api'
import { getProviderName } from '@/utils/providerMapping'
import { COMMON_STYLES } from '@/styles/globalStyles'
import { useAuthStore } from '@/store/authStore'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography
  // Tabs.TabPane 已弃用，改用 items

const HistoryPage: React.FC = () => {
  const { message } = App.useApp()
  const [searchParams] = useSearchParams()
  
  // 从URL参数获取初始tab值
  const initialTab = searchParams.get('type') || 'all'
  const [activeTab, setActiveTab] = useState(initialTab)
  const [filterStatus, setFilterStatus] = useState('all')
  const [dateRange, setDateRange] = useState<any[]>([])

  const [data, setData] = useState<any[]>([])
  const [videoTotal, setVideoTotal] = useState<number>(0)
  const [imageTotal, setImageTotal] = useState<number>(0)
  const [paginationTotal, setPaginationTotal] = useState<number>(0) // 当前分页的总数（用于分页组件）
  const [page, setPage] = useState<number>(1)
  const [limit, setLimit] = useState<number>(10)
  const [syncingIds, setSyncingIds] = useState<Set<string>>(new Set())
  const [refreshKey, setRefreshKey] = useState(0) // 用于强制刷新列表
  const [failedMediaIds, setFailedMediaIds] = useState<Set<string>>(new Set()) // 跟踪加载失败的媒体 ID
  
  // 视频预览Modal状态
  const [videoPreviewModal, setVideoPreviewModal] = useState({
    visible: false,
    videoUrl: '',
    prompt: ''
  })
  
  // 图片预览Modal状态
  const [imagePreviewModal, setImagePreviewModal] = useState({
    visible: false,
    imageUrls: [] as string[],
    prompt: ''
  })

  // 加载真实数据（视频+图片）
  const [isLoading, setIsLoading] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767)
  
  const loadData = async () => {
    // 避免重复加载
    if (isLoading) {
      console.log('⏸️  [历史列表] 已有加载任务进行中，跳过')
      return
    }
    
    try {
      // 只在首次加载时显示加载状态，避免切换页面时抖动
      if (data.length === 0) {
        setIsLoading(true)
      }
      console.log('📥 [历史列表] 开始加载数据...')
      
      // 使用统一的 /history 接口，后端已实现联合分页
      const response = await apiClient.get(`/user/history?type=${activeTab}&page=${page}&limit=${limit}${filterStatus !== 'all' ? `&status=${filterStatus.toUpperCase()}` : ''}`)
      
      if (!response.success) {
        console.error('❌ [历史列表] 请求失败:', response)
        return
      }

      // 读取分页总数（用于统计和分页）
      const paginationTotalFromApi = response.data?.pagination?.total ?? 0
      setPaginationTotal(paginationTotalFromApi) // 保存当前分页的总数
      
      if (activeTab === 'all' && response.data?.summary) {
        // 全部类型：使用 summary 获取分别的总数
        const summary = response.data.summary
        setVideoTotal(summary.videoTotal ?? 0)
        setImageTotal(summary.imageTotal ?? 0)
      } else {
        // 视频或图片类型：使用 pagination.total
        if (activeTab === 'video') {
          setVideoTotal(paginationTotalFromApi)
          setImageTotal(0)
        } else {
          setVideoTotal(0)
          setImageTotal(paginationTotalFromApi)
        }
      }

      // 页码越界保护：若当前页超过总页数，自动回退到最后一页
      const totalPages = Math.max(1, Math.ceil(paginationTotalFromApi / limit))
      if (paginationTotalFromApi > 0 && page > totalPages) {
        console.warn(`⚠️  [历史列表] 当前页(${page})超过总页数(${totalPages})，回退到最后一页`)
        setPage(totalPages)
        return
      }

      const vlist = (response.data?.videoGenerations || []).map((g: any) => {
        if (g.status?.toLowerCase() === 'completed' && !g.outputVideo) {
          console.warn('⚠️  [历史列表] 已完成但无视频URL:', {
            id: g.id,
            status: g.status,
            outputVideo: g.outputVideo
          })
        }
        
        return {
          id: g.id,
          type: 'video',
          prompt: g.prompt,
          status: g.status?.toLowerCase(),
          thumbnail: null,
          result: g.outputVideo,
          createdAt: g.createdAt,
          model: g.model,
          duration: g.duration,
          cost: g.cost,
        }
      })
        
      const ilist = (response.data?.imageGenerations || []).map((g: any) => {
        let outputImages = []
        try {
          outputImages = typeof g.outputImages === 'string' ? JSON.parse(g.outputImages) : (Array.isArray(g.outputImages) ? g.outputImages : [])
        } catch {}
        const statusLower = g.status?.toLowerCase()
        const completedCount = Array.isArray(outputImages) ? outputImages.length : 0
        const displayCount = statusLower === 'completed' ? completedCount : (g.count || 0)
        return {
          id: g.id,
          type: 'image',
          prompt: g.prompt,
          status: statusLower,
          thumbnail: outputImages[0] || undefined,
          result: outputImages,
          createdAt: g.createdAt,
          model: g.model,
          count: displayCount,
          cost: g.cost,
        }
      })
      
      const allData = [...vlist, ...ilist].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      // 直接更新数据，不使用 requestAnimationFrame，避免动画抖动
      setData(allData)
      // 清理失败状态，让新加载的数据可以重新尝试加载
      setFailedMediaIds(new Set())
      console.log(`✅ [历史列表] 数据加载完成: ${vlist.length}个视频, ${ilist.length}个图片`)
    } catch (e: any) {
      console.error('❌ [历史列表] 数据加载失败:', e.message || e)
      if (e?.response?.status === 403) {
        message.warning('登录已过期，请重新登录')
      }
    } finally {
      // 延迟设置加载状态为 false，确保数据已渲染
      setTimeout(() => {
        setIsLoading(false)
      }, 100)
    }
  }

  useEffect(() => {
    loadData()
    
    // 每30秒自动刷新一次数据（延长间隔，避免频繁超时）
    const timer = setInterval(() => {
      console.log('🔄 [历史列表] 定时刷新数据...')
      loadData()
    }, 30000)
    
    return () => clearInterval(timer)
  }, [page, limit, filterStatus, activeTab])

  // 监听窗口大小变化，更新移动端状态
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // 切换类型或状态时重置到第1页
  useEffect(() => {
    setPage(1)
  }, [activeTab, filterStatus])

  // 实时合并：当有任务完成或进行中状态变化 - 优化更新逻辑，减少抖动
  useEffect(() => {
    const merge = (p: any) => {
      console.log('🔔 [历史列表] 收到事件更新:', p)
      
      setData(prev => {
        const idx = prev.findIndex((i: any) => i.id === p.id)
        if (idx >= 0) {
          // 更新现有记录 - 使用浅拷贝避免整个数组重新创建
          const currentItem = prev[idx]
          const newStatus = p.status?.toLowerCase()
          const newResult = p.outputVideo || p.outputImages || currentItem.result
          
          // 如果状态和结果都没有变化，不更新，避免不必要的重新渲染
          if (currentItem.status === newStatus && currentItem.result === newResult) {
            return prev
          }
          
          const updated = {
            ...currentItem,
            status: newStatus,
            result: newResult,
          }
          
          console.log('✅ [历史列表] 更新记录:', {
            id: p.id,
            oldStatus: currentItem.status,
            newStatus: updated.status,
            oldResult: currentItem.result,
            newResult: updated.result
          })
          
          // 创建新数组，但只更新变化的项
          const next = [...prev]
          next[idx] = updated
          return next
        }
        
        // 新增记录 - 只在当前页面是第1页时添加到列表顶部
        if (page === 1) {
          const entry = { 
            id: p.id, 
            type: p.type || (p.outputImages ? 'image' : 'video'), 
            status: p.status?.toLowerCase(), 
            result: p.outputVideo || p.outputImages, 
            createdAt: new Date().toISOString(), 
            prompt: p.prompt || '' 
          }
          
          console.log('➕ [历史列表] 新增记录:', entry)
          return [entry, ...prev]
        }
        
        // 不在第1页时，不添加新记录，避免抖动
        return prev
      })
    }
    onEvent('video_updated', merge)
    onEvent('image_updated', merge)
  }, [page])

  // 根据activeTab和filterStatus过滤数据 - 使用 useMemo 优化性能
  const filteredHistory = useMemo(() => {
    return data.filter(item => {
      const typeMatch = activeTab === 'all' || 
        (activeTab === 'video' && item.type === 'video') ||
        (activeTab === 'image' && item.type === 'image')
      
      const statusMatch = filterStatus === 'all' || item.status === filterStatus
      
      return typeMatch && statusMatch
    })
  }, [data, activeTab, filterStatus])
  
  // 调试: 监控数据变化
  useEffect(() => {
    console.log(`📊 [前端] 数据状态更新:`, {
      总数: data.length,
      过滤后: filteredHistory.length,
      当前Tab: activeTab,
      当前筛选: filterStatus
    })
  }, [data, filteredHistory.length, activeTab, filterStatus])


  // 预览视频（使用Modal居中展示）
  const handlePreviewVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    setVideoPreviewModal({
      visible: true,
      videoUrl: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭视频预览Modal
  const handleCloseVideoPreview = () => {
    setVideoPreviewModal({
      visible: false,
      videoUrl: '',
      prompt: ''
    })
  }
  
  // 打开图片预览Modal
  const handlePreviewImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    setImagePreviewModal({
      visible: true,
      imageUrls: item.result,
      prompt: item.prompt || '未命名作品'
    })
  }
  
  // 关闭图片预览Modal
  const handleCloseImagePreview = () => {
    setImagePreviewModal({
      visible: false,
      imageUrls: [],
      prompt: ''
    })
  }

  // 检测是否为移动设备
  const isMobileDevice = () => {
    return /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  }

  // 检测是否为iOS设备
  const isIOSDevice = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
  }

  // 下载文件
  const handleDownload = async (url: string, filename: string, isVideo: boolean = false) => {
    try {
      message.loading('正在准备下载...', 0)
      
      // 移动端特殊处理
      if (isMobileDevice()) {
        if (isVideo) {
          // 移动端视频：打开新窗口，提示用户长按保存
          window.open(url, '_blank')
          message.destroy()
          if (isIOSDevice()) {
            message.info('视频已打开，请长按视频选择"保存到相册"', 5)
          } else {
            message.info('视频已打开，请点击右上角菜单选择"下载"或"保存"', 5)
          }
        } else {
          // 移动端图片：尝试直接下载，如果失败则打开新窗口
          try {
            const response = await fetch(url)
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = filename
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(blobUrl)
            message.destroy()
            message.success('下载成功，请查看相册或下载文件夹')
          } catch (fetchError) {
            // 如果下载失败，打开新窗口让用户手动保存
            window.open(url, '_blank')
            message.destroy()
            message.info('图片已打开，请长按图片选择"保存到相册"', 5)
          }
        }
        return
      }
      
      // PC端：使用 fetch + blob 下载
      try {
        const response = await fetch(url)
        const blob = await response.blob()
        
        // 创建本地 Blob URL
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = filename
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // 清理资源
        window.URL.revokeObjectURL(blobUrl)
        
        message.destroy()
        message.success('下载成功')
      } catch (fetchError) {
        // 如果 fetch 失败（可能是 CORS 问题），使用新窗口打开
        console.warn('Fetch下载失败，切换到新窗口下载:', fetchError)
        window.open(url, '_blank')
        message.destroy()
        message.success('已在新窗口打开下载')
      }
    } catch (error) {
      console.error('下载失败:', error)
      message.destroy()
      message.error('下载失败，请重试')
    }
  }

  // 下载视频
  const handleDownloadVideo = (item: any) => {
    if (!item.result) {
      message.warning('视频文件不存在')
      return
    }
    
    const filename = `${item.prompt || '未命名作品'}_${item.id}.mp4`.replace(/[^\w\s-]/g, '')
    handleDownload(item.result, filename, true) // 传递 isVideo=true
  }

  // 下载图片
  const handleDownloadImage = (item: any) => {
    if (!item.result || item.result.length === 0) {
      message.warning('图片文件不存在')
      return
    }
    
    if (item.result.length === 1) {
      // 单张图片
      const filename = `${item.prompt || '未命名作品'}_${item.id}.png`.replace(/[^\w\s-]/g, '')
      handleDownload(item.result[0], filename)
    } else {
      // 多张图片，逐个下载
      item.result.forEach((url: string, index: number) => {
        const filename = `${item.prompt || '未命名作品'}_${item.id}_${index + 1}.png`.replace(/[^\w\s-]/g, '')
        setTimeout(() => {
          handleDownload(url, filename)
        }, index * 500) // 延迟500ms避免浏览器阻止
      })
      message.success(`开始下载 ${item.result.length} 张图片...`)
    }
  }

  // 收藏/取消收藏
  const handleFavorite = async (item: any) => {
    try {
      // 这里可以调用后端API来实现收藏功能
      // 暂时用本地存储模拟
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      const existingIndex = favorites.findIndex((fav: any) => fav.id === item.id)
      
      if (existingIndex >= 0) {
        // 取消收藏
        favorites.splice(existingIndex, 1)
        message.success('已取消收藏')
      } else {
        // 添加收藏
        favorites.push({
          id: item.id,
          type: item.type,
          prompt: item.prompt,
          result: item.result,
          createdAt: item.createdAt,
          favoritedAt: new Date().toISOString()
        })
        message.success('已添加到收藏')
      }
      
      localStorage.setItem('favorites', JSON.stringify(favorites))
    } catch (error) {
      console.error('收藏操作失败:', error)
      message.error('操作失败，请重试')
    }
  }

  // 单个删除
  const handleDeleteItem = async (item: any) => {
    try {
      const endpoint = item.type === 'video' ? `/video/generations/${item.id}` : `/image/generations/${item.id}`
      await apiClient.delete(endpoint)
      
      message.success('删除成功')
      
      // 更新本地数据
      setData(prev => prev.filter(d => d.id !== item.id))

      // 重新加载数据以同步总量/分页
      await loadData()
    } catch (error: any) {
      console.error('删除失败:', error)
      message.error('删除失败: ' + (error?.message || '未知错误'))
    }
  }

  // 检查是否已收藏
  const isFavorited = (item: any) => {
    try {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]')
      return favorites.some((fav: any) => fav.id === item.id)
    } catch {
      return false
    }
  }

  // 同步单个任务状态
  const handleSyncStatus = async (id: string, type: string) => {
    setSyncingIds(prev => new Set(prev).add(id))
    
    try {
      const endpoint = type === 'video' ? `/video/generations/${id}/sync` : `/image/generations/${id}/sync`
      console.log(`🔄 [前端] 开始同步任务:`, { id, type, endpoint })
      
      const response = await apiClient.post(endpoint)
      
      console.log(`📥 [前端] 同步响应 (完整):`, response)
      console.log(`📥 [前端] response.data:`, response.data)
      
      // 正确解析响应格式
      // 后端返回: { success: true, message: "...", data: { status: "...", ... } }
      const apiResponse = response.data
      const isSuccess = apiResponse.success !== false
      const msg = apiResponse.message || '同步成功'
      const responseData = apiResponse.data || {}
      
      console.log(`📊 [前端] 解析结果:`, { 
        isSuccess, 
        msg, 
        responseData 
      })
      
      if (isSuccess) {
        // 显示提示消息
        if (responseData.status === 'FAILED') {
          // 如果是无法恢复的失败任务，显示更明确的提示
          if (responseData.cannotRecover) {
            message.error({
              content: msg,
              duration: 6
            })
          } else {
            message.warning(msg)
          }
        } else if (responseData.status === 'COMPLETED') {
          message.success(msg)
        } else {
          message.info(msg)
        }
        
        // 更新本地数据 - 使用函数式更新确保获取最新状态
        console.log(`📋 [前端] 开始更新数据，查找 ID: ${id}`)
        
        setData(prevData => {
          console.log(`📋 [前端] setData 回调执行，当前 data 总数: ${prevData.length}`)
          
          const updatedData = prevData.map((item, index) => {
            if (item.id === id) {
              const newStatus = (responseData.status || 'failed').toLowerCase()
              const newResult = responseData.outputVideo || responseData.outputImages || item.result
              
              console.log(`✅ [前端] 找到匹配项 (索引 ${index}):`, { 
                itemId: item.id,
                oldStatus: item.status, 
                newStatus,
                oldResult: item.result,
                newResult,
                hasResult: !!newResult,
                creditsRefunded: responseData.creditsRefunded
              })
              
              const updated = {
                ...item,
                status: newStatus,
                result: newResult
              }
              
              console.log(`🔄 [前端] 更新后的数据:`, updated)
              return updated
            }
            return item
          })
          
          console.log(`🔄 [前端] 返回新数据数组，总数: ${updatedData.length}`)
          return updatedData
        })
        
        console.log(`✅ [前端] setData 已调用`)
        
        // 强制刷新列表渲染
        const newRefreshKey = Date.now()
        setRefreshKey(newRefreshKey)
        console.log(`🔄 [前端] 强制刷新列表渲染, refreshKey: ${newRefreshKey}`)
        
        // 根据不同情况决定是否刷新页面
        if (responseData.creditsRefunded && responseData.creditsRefunded > 0) {
          // 🔥 修复：退还了积分，刷新用户信息而不是整页刷新
          console.log(`💰 [前端] 积分已退还: ${responseData.creditsRefunded}，刷新用户积分`)
          try {
            await useAuthStore.getState().refreshUser();
            console.log(`✅ [前端] 用户积分已刷新`)
          } catch (refreshError) {
            console.warn('⚠️ [前端] 刷新用户积分失败，使用整页刷新:', refreshError);
            setTimeout(() => {
              window.location.reload()
            }, 1500)
          }
        } else if (responseData.status === 'COMPLETED') {
          // 任务完成，刷新页面显示新按钮
          console.log(`🎬 [前端] 任务完成，2秒后刷新页面以显示预览按钮`)
          setTimeout(() => {
            window.location.reload()
          }, 2000)
        } else if (responseData.alreadyRefunded || responseData.status === 'FAILED') {
          // 已失败且积分之前已退还，刷新页面确保UI同步
          console.log(`ℹ️  [前端] 任务已失败，2秒后刷新页面确保UI更新`)
          setTimeout(() => {
            console.log(`🔄 [前端] 强制刷新页面`)
            window.location.reload()
          }, 2000)
        } else {
          // 仍在处理中，不刷新页面
          console.log(`✅ [前端] 状态已更新为: ${responseData.status}，无需刷新页面`)
        }
      }
    } catch (error: any) {
      console.error(`❌ [前端] 同步失败:`, error)
      console.error(`❌ [前端] 错误响应:`, error?.response?.data)
      
      const errorMsg = error?.response?.data?.message || error?.message || '未知错误'
      message.error({
        content: '同步失败: ' + errorMsg,
        duration: 5
      })
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  // 渲染视频项 - 使用固定结构避免抖动
  const renderVideoItem = (item: any) => (
    <div>
      <Card 
        className="unified-card history-item-card border border-gray-100"
        hoverable={false}
      >
        <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
          {/* 移动端占满整行，避免缩略图过小；桌面保持原比例 */}
          <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
            <div 
              className="relative group history-thumb-wrapper"
              onClick={() => {
                // 如果视频已完成，点击缩略图即可预览
                if (item.status === 'completed' && item.result) {
                  handlePreviewVideo(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default',
                width: '100%',
                height: '144px',
                minHeight: '144px',
                aspectRatio: '16/9',
                overflow: 'hidden'
              }}
            >
              {item.result && !failedMediaIds.has(item.id) ? (
                <video
                  className="w-full h-full rounded-lg shadow-sm"
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                  src={item.result}
                  preload="metadata"
                  muted
                  playsInline
                  onMouseEnter={(e) => {
                    const video = e.currentTarget
                    video.play().catch(() => {})
                  }}
                  onMouseLeave={(e) => {
                    const video = e.currentTarget
                    video.pause()
                    video.currentTime = 0
                  }}
                  onError={() => {
                    // 使用 React 状态管理，而不是直接操作 DOM
                    setFailedMediaIds(prev => new Set(prev).add(item.id))
                  }}
                />
              ) : (
                <div 
                  className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center shadow-sm history-thumb-fallback" 
                  style={{ 
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    minHeight: '144px'
                  }}
                >
                  {failedMediaIds.has(item.id) ? (
                    <svg className="w-10 h-10 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v4a1 1 0 002 0V7zm-1 8a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" clipRule="evenodd" />
                    </svg>
                  ) : (
                  <VideoCameraOutlined style={{ fontSize: 'var(--font-h2-mobile)' }} className="text-blue-400" />
                  )}
                </div>
              )}
              {item.status === 'processing' && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <div className="text-white text-center">
                    <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full mx-auto mb-3"></div>
                    <Text className="text-white text-sm font-medium">生成中...</Text>
                  </div>
                </div>
              )}
              {item.status === 'completed' && item.result && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg"></div>
                </>
              )}
            </div>
          </Col>
          {/* 左右留同样的内边距，保持与缩略图列对齐 */}
          <Col xs={24} sm={16} md={18} className="pl-3 pr-3 md:pl-4 md:pr-4 history-info-col">
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
                </Paragraph>
                <Space wrap style={{ marginBottom: 'var(--spacing-md)' }} size="small">
                  <Tag color="blue" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <VideoCameraOutlined /> {getProviderName(item.model, 'video')}
                  </Tag>
                </Space>
              </div>
              <div className="flex items-center justify-between mt-3">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : item.status === 'failed' ? 'error' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing'
                        ? '⏳ 处理中'
                        : item.status === 'failed'
                          ? (<><span>✗</span><span className="hidden sm:inline"> 违规</span></>)
                          : '⏸ 待处理'}
                    </Tag>
                  )}
                </Space>
                <div className="flex items-center gap-1 flex-nowrap">
                  {item.status === 'completed' && (
                    <>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewVideo(item)}
                        className="border-gray-300 flex-shrink-0"
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                          padding: '0 clamp(8px, 1.5vw, 12px)'
                        }}
                      >
                        <span className="hidden sm:inline">预览</span>
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadVideo(item)}
                        className="flex-shrink-0"
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                          padding: '0 clamp(8px, 1.5vw, 12px)'
                        }}
                      >
                        <span className="hidden sm:inline">下载</span>
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'video')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 flex-shrink-0"
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                        padding: '0 clamp(8px, 1.5vw, 12px)'
                      }}
                    >
                      <span className="hidden sm:inline">同步状态</span>
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    danger
                    onClick={() => handleDeleteItem(item)}
                    className="flex-shrink-0"
                    style={{ 
                      borderRadius: '20px', 
                      fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                      padding: '0 clamp(8px, 1.5vw, 12px)'
                    }}
                  >
                    <span className="hidden sm:inline">删除</span>
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )

  // 渲染图片项 - 使用固定结构避免抖动
  const renderImageItem = (item: any) => (
    <div>
      <Card 
        className="unified-card history-item-card mb-4 border border-gray-100"
        hoverable={false}
      >
        <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
          <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
            <div 
              className="relative group history-thumb-wrapper"
              onClick={() => {
                // 点击缩略图预览
                if (item.status === 'completed' && item.result && item.result.length > 0) {
                  handlePreviewImage(item)
                }
              }}
              style={{ 
                cursor: item.status === 'completed' && item.result ? 'pointer' : 'default',
                width: '100%',
                height: '144px',
                minHeight: '144px',
                aspectRatio: '1/1',
                overflow: 'hidden'
              }}
            >
              {!failedMediaIds.has(item.id) && (item.thumbnail || (item.result && item.result.length > 0)) ? (
                <img
                  src={item.thumbnail || item.result[0]}
                  alt="Generated image"
                  loading="lazy"
                  className="w-full h-full rounded-lg shadow-sm"
                  style={{ 
                    borderRadius: '12px',
                    objectFit: 'cover',
                    objectPosition: 'center',
                    width: '100%',
                    height: '100%',
                    display: 'block'
                  }}
                  onError={() => {
                    // 使用 React 状态管理，而不是直接操作 DOM
                    setFailedMediaIds(prev => new Set(prev).add(item.id))
                  }}
                />
              ) : (
                <div 
                  className="w-full h-full bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 rounded-lg flex items-center justify-center shadow-sm history-thumb-fallback" 
                  style={{ 
                    borderRadius: '12px',
                    width: '100%',
                    height: '100%',
                    minHeight: '144px'
                  }}
                >
                  {failedMediaIds.has(item.id) ? (
                    <svg className="text-4xl text-purple-400" fill="currentColor" viewBox="0 0 20 20" width="40" height="40">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                  ) : (
                  <PictureOutlined style={{ fontSize: 'var(--font-h2-mobile)' }} className="text-purple-400" />
                  )}
                </div>
              )}
              
              {/* 状态遮罩层 */}
              {item.status === 'processing' && (
                <div className="absolute inset-0 bg-black bg-opacity-60 rounded-xl flex items-center justify-center backdrop-blur-sm pointer-events-none">
                  <div className="text-white text-center">
                    <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full mx-auto mb-3"></div>
                    <Text className="text-white text-sm font-medium">生成中...</Text>
                  </div>
                </div>
              )}
              
              {/* 完成状态标记 */}
              {item.status === 'completed' && item.thumbnail && (
                <>
                  <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-lg pointer-events-none"></div>
                </>
              )}
            </div>
          </Col>
          <Col xs={24} sm={16} md={18} className="pl-3 md:pl-4 history-info-col">
            <div className="h-full flex flex-col justify-between">
              <div>
                <Paragraph 
                  ellipsis={{ rows: 2, expandable: false }}
                  className="mb-3 text-gray-800 font-semibold text-responsive-base"
                  style={{ lineHeight: 1.5 }}
                >
                  {item.prompt || '未命名作品'}
                </Paragraph>
                <Space wrap style={{ marginBottom: 'var(--spacing-md)' }} size="small">
                  <Tag color="purple" style={{ borderRadius: '12px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)' }}>
                    <PictureOutlined /> {getProviderName(item.model, 'image')}
                  </Tag>
                </Space>
              </div>
              <div className="flex items-center justify-between mt-3">
                <Space size="small">
                  {/* 只在非完成状态时显示状态标签 */}
                  {item.status !== 'completed' && (
                    <Tag 
                      color={item.status === 'processing' ? 'processing' : item.status === 'failed' ? 'error' : 'default'}
                      style={{ borderRadius: '12px' }}
                    >
                      {item.status === 'processing'
                        ? '⏳ 处理中'
                        : item.status === 'failed'
                          ? (<><span>✗</span><span className="hidden sm:inline"> 违规</span></>)
                          : '⏸ 待处理'}
                    </Tag>
                  )}
                </Space>
                <div className="flex items-center gap-1 flex-nowrap">
                  {item.status === 'completed' && (
                    <>
                      <Button 
                        size="small" 
                        icon={<EyeOutlined />}
                        onClick={() => handlePreviewImage(item)}
                        className="border-gray-300 flex-shrink-0"
                        style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', padding: '0 clamp(8px, 1.5vw, 12px)' }}
                      >
                        <span className="hidden sm:inline">
                          预览 {item.result && item.result.length > 1 ? `(${item.result.length}张)` : ''}
                        </span>
                      </Button>
                      <Button 
                        size="small" 
                        icon={<DownloadOutlined />} 
                        type="primary"
                        onClick={() => handleDownloadImage(item)}
                        className="flex-shrink-0"
                        style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', padding: '0 clamp(8px, 1.5vw, 12px)' }}
                      >
                        <span className="hidden sm:inline">下载</span>
                      </Button>
                    </>
                  )}
                  {(item.status === 'failed' || item.status === 'pending' || item.status === 'processing') && (
                    <Button 
                      size="small" 
                      icon={<SyncOutlined spin={syncingIds.has(item.id)} />}
                      onClick={() => handleSyncStatus(item.id, 'image')}
                      loading={syncingIds.has(item.id)}
                      className="border-orange-300 flex-shrink-0"
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.65rem, 1.5vw, 0.875rem)',
                        padding: '0 clamp(8px, 1.5vw, 12px)'
                      }}
                    >
                      <span className="hidden sm:inline">同步状态</span>
                    </Button>
                  )}
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    danger
                    onClick={() => handleDeleteItem(item)}
                    className="flex-shrink-0"
                    style={{ borderRadius: '20px', fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', padding: '0 clamp(8px, 1.5vw, 12px)' }}
                  >
                    <span className="hidden sm:inline">删除</span>
                  </Button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )

  // 骨架屏组件
  const renderSkeletonItem = () => (
    <Card 
      className="border border-gray-100 unified-card"
      style={{
        marginBottom: 'var(--spacing-lg)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        minHeight: '180px'
      }}
      styles={{ body: { padding: '20px' } }}
    >
      <Row gutter={[0, 0]} className="history-item-row md:!mx-[-10px]">
        <Col xs={24} sm={8} md={6} className="history-thumb-col md:!px-2.5">
          <Skeleton.Image 
            active 
            style={{ 
              width: '100%', 
              height: '144px', 
              minHeight: '144px',
              borderRadius: '12px'
            }} 
          />
        </Col>
        <Col xs={24} sm={16} md={18} className="pl-3 pr-3 md:pl-4 md:pr-4 history-info-col">
          <Skeleton 
            active 
            paragraph={{ rows: 3 }} 
            title={{ width: '80%' }}
          />
        </Col>
      </Row>
    </Card>
  )

  return (
    <div className="history-page min-h-screen w-full relative">
      {/* 禁用历史页内的过渡动画（作用域到 history-page，避免全局影响） */}
      <style>{`
        .history-page .ant-list-item,
        .history-page .history-pagination,
        .history-page .history-pagination *,
        .history-page .history-item-row,
        .history-page .history-item-row * {
          transition: none !important;
          animation: none !important;
          transform: none !important;
        }

        /* 统一历史卡片风格 */
        .history-page .history-card {
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .history-page .history-filter-bar {
          border-radius: 999px;
          background: rgba(249, 250, 251, 0.9);
        }
      `}</style>
      {/* 移动端背景装饰 */}
      <div className="md:hidden absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-gray-100/50 to-transparent pointer-events-none page-mobile-hero"></div>
      <div
        className="unified-container relative z-10"
        style={{
          paddingTop: 'var(--spacing-sm)',
        }}
      >
        <div className="flex flex-col">
          {/* 头部设计 - 左右结构 */}
          <div className="w-full page-header-section">
            <div
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-lg)',
                gap: 'var(--spacing-md)'
              }}
            >
            {/* 左侧：标题和副标题 */}
            <div className="flex-1">
              <div
                className="flex items-center"
                style={{
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-xs)',
                }}
              >
                <span style={{ fontSize: 'var(--font-h1-mobile)' }}>📚</span>
                <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  我的作品库
                </h1>
              </div>
              <Paragraph
                className="unified-text-secondary"
                style={{
                  fontSize: 'var(--font-body)',
                  marginBottom: 0,
                }}
              >
                查看、管理和下载您的所有AI创作
              </Paragraph>
            </div>
            
            {/* 右侧：特色标签 */}
            <div className="history-header-tags flex flex-wrap gap-2 md:justify-end">
              <Tag icon={<HistoryOutlined />} className="unified-tag bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700">
                {videoTotal + imageTotal} 作品
              </Tag>
              <Tag icon={<VideoCameraOutlined />} className="unified-tag bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700">
                {videoTotal} 视频
              </Tag>
              <Tag icon={<PictureOutlined />} className="unified-tag bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700">
                {imageTotal} 图片
              </Tag>
            </div>
          </div>
          </div>


          {/* 筛选器 */}
          <Card 
            className="unified-card border border-gray-100 history-card" 
            style={{
              marginBottom: 'var(--spacing-md)',
            }}
            styles={{
              body: {
                padding: 'var(--spacing-sm) var(--spacing-md)',
              }
            }}
          >
            {/* 移动端顶部装饰条，参考视频/图像生成页主卡片风格 */}
            <div className="md:hidden bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1.5 w-full rounded-full mb-3"></div>
            <div className="history-filter-bar flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              {/* 左侧：筛选器 */}
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">类型</Text>
                  <Space size="small">
                    <Button
                      type={activeTab === 'all' ? 'primary' : 'default'}
                      onClick={() => setActiveTab('all')}
                      className={activeTab === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      全部
                    </Button>
                    <Button
                      type={activeTab === 'video' ? 'primary' : 'default'}
                      icon={<VideoCameraOutlined />}
                      onClick={() => setActiveTab('video')}
                      className={activeTab === 'video' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      视频
                    </Button>
                    <Button
                      type={activeTab === 'image' ? 'primary' : 'default'}
                      icon={<PictureOutlined />}
                      onClick={() => setActiveTab('image')}
                      className={activeTab === 'image' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      图像
                    </Button>
                  </Space>
                </div>
                
                <div className="flex items-center gap-3">
                  <Text strong className="text-gray-700 text-responsive-sm">状态</Text>
                  <Space size="small">
                    <Button
                      type={filterStatus === 'all' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('all')}
                      size="small"
                      className={filterStatus === 'all' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      全部
                    </Button>
                    {!isMobile && (
                      <Button
                        type={filterStatus === 'processing' ? 'primary' : 'default'}
                        onClick={() => setFilterStatus('processing')}
                        size="small"
                        className={filterStatus === 'processing' ? '' : 'border-gray-300'}
                        style={{ 
                          borderRadius: '20px', 
                          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                          transition: 'none'
                        }}
                      >
                        处理中
                      </Button>
                    )}
                    <Button
                      type={filterStatus === 'completed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('completed')}
                      size="small"
                      className={filterStatus === 'completed' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      已完成
                    </Button>
                    <Button
                      type={filterStatus === 'failed' ? 'primary' : 'default'}
                      onClick={() => setFilterStatus('failed')}
                      size="small"
                      className={filterStatus === 'failed' ? '' : 'border-gray-300'}
                      style={{ 
                        borderRadius: '20px', 
                        fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
                        transition: 'none'
                      }}
                    >
                      失败
                    </Button>
                  </Space>
                </div>
              </div>
              
              {/* 右侧：统计 */}
              <div className="flex items-center gap-2 md:gap-4 md:justify-end">
                <Text strong className="text-gray-700 text-responsive-sm">
                  共 <span className="text-blue-600 font-bold">{
                    activeTab === 'video' ? videoTotal : activeTab === 'image' ? imageTotal : (videoTotal + imageTotal)
                  }</span> 条记录
                </Text>
              </div>
            </div>
          </Card>

          {/* 内容列表 */}
          <Card 
            className="unified-card border border-gray-100 history-card"
            styles={{ 
              body: { 
                padding: 0,
              } 
            }}
          >
            {/* 顶部装饰渐变线 */}
            <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1 w-full"></div>

            {isLoading && data.length === 0 ? (
              // 初始加载时显示骨架屏
              <div style={{ padding: '12px' }}>
                {Array.from({ length: limit }).map((_, index) => (
                  <div key={`skeleton-${index}`} style={{ marginBottom: 'var(--spacing-lg)' }}>
                    {renderSkeletonItem()}
                  </div>
                ))}
              </div>
            ) : filteredHistory.length === 0 ? (
              <div style={{ padding: '40px 12px' }}>
              <Empty
                  description={
                    <div>
                      <Text className="text-gray-500 text-responsive-sm">暂无生成记录</Text>
                      <br />
                      <Text className="text-gray-400 text-xs mt-2">
                        开始创作您的第一个AI作品吧
                      </Text>
                    </div>
                  }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
              </div>
            ) : (
              <List
                key={refreshKey}
                dataSource={filteredHistory}
                loading={isLoading && data.length > 0}
                split={false}
                style={{ transition: 'none' }}
                pagination={{ 
                  current: page,
                  pageSize: limit,
                  total: paginationTotal, // 直接使用后端返回的分页总数
                  showSizeChanger: !isMobile, // 移动端隐藏每页条数选择器
                  pageSizeOptions: ['10','20'],
                  showQuickJumper: true,
                  showLessItems: isMobile, // 移动端仅保留前后少量页码
                  onChange: (p) => {
                    const totalPages = Math.max(1, Math.ceil(paginationTotal / limit))
                    const next = Math.min(Math.max(1, p), totalPages)
                    if (next !== page) setPage(next)
                    // 立即滚动到列表顶部，不使用平滑滚动
                    try { window.scrollTo({ top: 0, behavior: 'auto' }) } catch {}
                  },
                  onShowSizeChange: (p, size) => { setPage(1); setLimit(size); try { window.scrollTo({ top: 0, behavior: 'auto' }) } catch {} },
                  showTotal: isMobile ? undefined : (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`, // 移动端隐藏总数显示
                  // 移动端不使用 simple 模式，显示完整分页按钮
                  style: { textAlign: 'center', padding: '12px 0' },
                  className: 'history-pagination'
                }}
                renderItem={(item) => {
                  // 使用 useCallback 优化渲染函数
                  return (
                    <List.Item className="!px-0 !border-0">
                      <div className="w-full">
                        {item.type === 'video' ? renderVideoItem(item) : renderImageItem(item)}
                      </div>
                    </List.Item>
                  )
                }}
              />
            )}
          </Card>
        </div>
      </div>
      
      {/* 视频预览Modal */}
      <Modal
        open={videoPreviewModal.visible}
        onCancel={handleCloseVideoPreview}
        footer={null}
        width="auto"
        centered
        destroyOnHidden
        styles={{ body: { padding: 'var(--spacing-md)' } }}
        className="preview-modal"
      >
        <div className="text-center preview-modal-content">
          <video
            controls
            autoPlay
            className="preview-media"
            style={{
              maxWidth: '90vw',
              maxHeight: '80vh',
              width: 'auto',
              height: 'auto',
              borderRadius: '8px',
              backgroundColor: '#000',
              objectFit: 'contain',
              objectPosition: 'center',
              display: 'block',
              margin: '0 auto',
              flexShrink: 0,
              flexGrow: 0,
              boxSizing: 'content-box'
            }}
          >
            <source src={videoPreviewModal.videoUrl} type="video/mp4" />
            您的浏览器不支持视频播放。
          </video>
          
          {/* 底部操作按钮 */}
          <div className="preview-modal-footer">
            <Space size="middle" className="w-full preview-modal-buttons">
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />}
                onClick={() => {
                  handleDownload(videoPreviewModal.videoUrl, `${videoPreviewModal.prompt}.mp4`, true)
                }}
                className="preview-download-btn"
              >
                <span>下载视频</span>
              </Button>
              <Button 
                size="large"
                onClick={handleCloseVideoPreview}
                className="preview-close-btn"
              >
                <span>关闭</span>
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
      
      {/* 图片预览Modal */}
      <Modal
        open={imagePreviewModal.visible}
        onCancel={handleCloseImagePreview}
        footer={null}
        width="auto"
        centered
        destroyOnHidden
        styles={{ body: { padding: 'var(--spacing-md)' } }}
        className="preview-modal"
      >
        <div className="text-center preview-modal-content">
          {/* 图片显示 */}
          <div className="preview-media-container">
            {imagePreviewModal.imageUrls.length === 1 ? (
              // 单张图片
              <img
                src={imagePreviewModal.imageUrls[0]}
                alt="预览图片"
                className="preview-media"
                style={{
                  maxWidth: '90vw',
                  maxHeight: '80vh',
                  width: 'auto',
                  height: 'auto',
                  borderRadius: '8px',
                  display: 'block',
                  margin: '0 auto',
                  objectFit: 'contain',
                  objectPosition: 'center',
                  flexShrink: 0,
                  flexGrow: 0,
                  minWidth: 0,
                  minHeight: 0,
                  boxSizing: 'content-box'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f3f4f6" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="16"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                }}
              />
            ) : (
              // 多张图片 - 横向滚动
              <div>
                <div className="flex gap-3 justify-center overflow-x-auto pb-4" style={{ maxWidth: '100%' }}>
                  {imagePreviewModal.imageUrls.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`图片 ${index + 1}`}
                      style={{
                        maxWidth: '90vw',
                        maxHeight: '60vh',
                        width: 'auto',
                        height: 'auto',
                        borderRadius: '8px',
                        border: '2px solid #e5e7eb',
                        cursor: 'pointer',
                        objectFit: 'contain',
                        aspectRatio: 'auto',
                        flexShrink: 0
                      }}
                      onClick={(e) => {
                        // 点击切换为主图
                        const newUrls = [...imagePreviewModal.imageUrls];
                        [newUrls[0], newUrls[index]] = [newUrls[index], newUrls[0]];
                        setImagePreviewModal({ ...imagePreviewModal, imageUrls: newUrls });
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  ))}
                </div>
                <div className="text-gray-500 text-sm mb-2">
                  共 {imagePreviewModal.imageUrls.length} 张图片，点击切换查看
                </div>
              </div>
            )}
          </div>
          
          {/* 底部操作按钮 - 与视频预览保持一致 */}
          <div className="preview-modal-footer">
            <Space size="middle" className="w-full preview-modal-buttons">
              <Button 
                type="primary" 
                size="large"
                icon={<DownloadOutlined />}
                onClick={() => {
                  // 下载所有图片
                  imagePreviewModal.imageUrls.forEach((url, index) => {
                    setTimeout(() => {
                      handleDownload(url, `${imagePreviewModal.prompt}_${index + 1}.jpg`)
                    }, index * 500);
                  });
                }}
                className="preview-download-btn"
              >
                <span>下载图片</span>
              </Button>
              <Button 
                size="large"
                onClick={handleCloseImagePreview}
                className="preview-close-btn"
              >
                <span>关闭</span>
              </Button>
            </Space>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default HistoryPage